import { useMemo, useRef } from "react"
import type { BinaryOperator, Filter, Query } from "@cubejs-client/core"
import { useCubeQuery } from "~/ui-rtk/hooks/cube"

const PREFIX = "mega_feed"

// Everything the feed knows about a product. The grid can't show these — long
// text and image arrays would wreck the row layout — so the drawer is the only
// place they surface.
const TEXT_DIMENSIONS = [
  "id",
  "item_group_id",
  "parent_title",
  "parent_title_no_color",
  "title",
  "description",
  "header_description",
  "product_detail_description",
  "specs",
  "link",
  "brand",
  "product_type",
  "type",
  "google_product_category",
  "condition",
  "age_group",
  "gender",
  "adult",
  "availability",
  "quantity",
  "stock",
  "total_variants",
  "variant_count",
  "price",
  "sale_price",
  "price_currency",
  "gtin",
  "mpn",
  "id_sku",
  "tags",
  "collections",
  "shipping_country",
  "taxable",
  "tax_code",
  "image_count",
]

// Named single-image slots, in the order the drawer renders them.
export const IMAGE_SLOTS = [
  { key: "main_image_link", label: "Main" },
  { key: "secondary_image_link", label: "Secondary" },
  { key: "lifestyle_image_link", label: "Lifestyle" },
  { key: "secondary_lifestyle_image_link", label: "Lifestyle 2" },
  { key: "detail_image_link", label: "Detail" },
  { key: "secondary_detail_image_link", label: "Detail 2" },
  { key: "model_studio_image_link", label: "Model / Studio" },
  { key: "image_link", label: "Feed Primary" },
]

// Repeatable image sets, each rendered as its own carousel.
export const IMAGE_SETS = [
  { key: "all_image_links", label: "All Images" },
  { key: "static_image_links", label: "Static / Packshot" },
  { key: "lifestyle_image_links", label: "Lifestyle" },
  { key: "detail_image_links", label: "Detail" },
  { key: "model_studio_image_links", label: "Model / Studio" },
  { key: "additional_image_links", label: "Additional" },
]

const METRICS = [
  "total_revenue",
  "sum_first_order_revs",
  "sum_second_order_revs",
  "sum_first_order_line_revenue",
  "sum_first_order_ltv_180_day",
  "sum_first_order_pre_mktg_contribution_180_day",
  "expected_new_customer_session_ltv",
  "total_orders",
  "first_orders",
  "repeat_orders",
  "first_order_aov",
  "sessions",
  "screen_page_views",
  "add_to_carts",
  "checkouts",
  "revenue_per_session",
  "conversion_rate",
  "add_to_cart_rate",
  "checkout_rate",
  "bounce_rate",
]

export const DETAIL_METRICS = METRICS

/**
 * ClickHouse array columns reach the client either as a real JS array (native
 * driver) or as the ClickHouse text form `['https://a','https://b']` (HTTP
 * interface). Normalize both, dropping nulls and blanks — the same rule the
 * feed's Python `_split_images` applies.
 */
export function parseImageArray(value: unknown): string[] {
  if (!value) return []

  const raw: unknown[] = Array.isArray(value)
    ? value
    : // Match single-quoted elements rather than splitting on ',', so a comma
      // inside a URL can't corrupt the list. Unquoted NULLs simply don't match.
      Array.from(String(value).matchAll(/'([^']*)'/g), match => match[1])

  return raw
    .filter((item): item is string => typeof item === "string")
    .map(item => item.trim())
    .filter(item => item.length > 0 && item.toUpperCase() !== "NULL")
}

export type TProductDetail = Record<string, unknown>

type LatchedRow = {
  /** The first row of the last result that actually resolved, if any. */
  row: Record<string, unknown> | null
  /** True once a result has resolved for the current key — `row: null` then
   *  means "resolved, zero rows", not "still waiting". */
  resolved: boolean
}

/**
 * Holds the last resolved row for `key` so a cancelled query can't erase it.
 *
 * `useCubeQuery` cancels its in-flight request whenever its effect re-runs —
 * which happens on every mount under StrictMode, and again whenever `skip` or
 * the query flips. A cancelled request does NOT reject: the client swallows its
 * mutex error and the promise resolves `undefined`, which the hook then writes
 * over `resultSet`. When the cancelled (cold, slower) response lands after the
 * winning one, a perfectly good 200 is replaced by `undefined` and every
 * `!resultSet` check reads it as "still loading" — forever.
 */
function useLatchedRow(
  key: string,
  resultSet: { rawData: () => Record<string, unknown>[] } | null | undefined,
): LatchedRow {
  const latch = useRef<LatchedRow & { key: string }>({
    key,
    row: null,
    resolved: false,
  })

  // A different product starts fresh — otherwise the previous product's row
  // would show while the new one loads.
  if (latch.current.key !== key) {
    latch.current = { key, row: null, resolved: false }
  }
  if (resultSet) {
    latch.current.row = resultSet.rawData()?.[0] ?? null
    latch.current.resolved = true
  }

  return latch.current
}

/**
 * Full feed row plus date-bounded sales metrics for a single product.
 * Grouping by product-constant dimensions collapses to one row.
 */
export default function useProductDetailQuery({
  itemGroupId,
  dateRange,
}: {
  itemGroupId: string
  dateRange: [string, string]
}) {
  const [startDate, endDate] = dateRange

  const productFilter = useMemo<Filter[]>(
    () => [
      {
        member: `${PREFIX}.item_group_id`,
        operator: "equals" as BinaryOperator,
        values: [itemGroupId],
      },
    ],
    [itemGroupId],
  )

  // Query 1 — ATTRIBUTES. Deliberately carries NO time dimension.
  //
  // md_date belongs to the GA4 cube on the far side of a LEFT JOIN, so a
  // dateRange filter on it discards products with no site activity in the
  // window — feed row, images and all. Requesting only feed dimensions means
  // Cube never joins the GA4 table here, so the product always resolves.
  const attributesQuery = useMemo<Query>(
    () => ({
      dimensions: [
        ...TEXT_DIMENSIONS,
        ...IMAGE_SLOTS.map(({ key }) => key),
        ...IMAGE_SETS.map(({ key }) => key),
      ].map(dimension => `${PREFIX}.${dimension}`),
      filters: productFilter,
      limit: 1,
    }),
    [productFilter],
  )

  // Query 2 — METRICS, bounded by the page's date range. Measures only, so the
  // join is emitted and the window applies. Returns nothing when the product had
  // no activity in range, which reads as "no sales", not "no product".
  const metricsQuery = useMemo<Query>(
    () => ({
      measures: METRICS.map(metric => `${PREFIX}.${metric}`),
      timeDimensions: [
        {
          dimension: `${PREFIX}.md_date`,
          dateRange: [startDate, endDate],
        },
      ],
      filters: productFilter,
    }),
    [productFilter, startDate, endDate],
  )

  const { resultSet: attributesResult, error: attributesError } = useCubeQuery(
    attributesQuery,
    { skip: !itemGroupId },
  )

  const { resultSet: metricsResult, error: metricsError } = useCubeQuery(
    metricsQuery,
    { skip: !itemGroupId },
  )

  // Read through the latch, never the raw resultSet — see useLatchedRow.
  const attributesLatch = useLatchedRow(itemGroupId, attributesResult)
  const metricsLatch = useLatchedRow(itemGroupId, metricsResult)

  // rawData(), not tablePivot(): rows are already flat records keyed
  // `mega_feed.<member>`, and tablePivot's pivot config is pure overhead here.
  const stripPrefix = (row: Record<string, unknown> | undefined) => {
    if (!row) return null
    const stripped: TProductDetail = {}
    Object.keys(row).forEach(key => {
      stripped[key.replace(`${PREFIX}.`, "")] = row[key]
    })
    return stripped
  }

  const attributes = useMemo(
    () => stripPrefix(attributesLatch.row ?? undefined),
    [attributesLatch.row],
  )

  const metrics = useMemo(
    () => stripPrefix(metricsLatch.row ?? undefined),
    [metricsLatch.row],
  )

  // Attributes drive existence; metrics are additive and may legitimately be
  // absent for a product with no activity in the window.
  const product = useMemo<TProductDetail | null>(
    () => (attributes ? { ...attributes, ...(metrics ?? {}) } : null),
    [attributes, metrics],
  )

  const error = attributesError ?? metricsError

  // "Not resolved yet" (in flight, or the capabilities gate is still skipping
  // while the capabilities set hydrates) is NOT "resolved with zero rows".
  // Conflating the two made the drawer claim the product didn't exist before it
  // had ever asked.
  const resolvedEmpty = attributesLatch.resolved && attributes === null

  return {
    product,
    isLoading: !attributesLatch.resolved && !error,
    /** True only when the metrics query came back empty but the product exists. */
    hasNoActivityInRange: metricsLatch.resolved && metrics === null,
    resolvedEmpty,
    error,
  }
}
